import { PublicKey } from "@solana/web3.js";
import {
  KYDEventDefaults,
  KYDEventSettlementItem,
  KYDEventSettlementItemBySource,
  KYDMarketingPlatform,
  KYDOrganizationDisplay,
  KYDOrganizationEventLink,
  KYDOrganizationFee,
  KYDOrganizationFull,
  KYDOrganizationSettings,
  TurnkeySubOrganization,
} from "./KYDOrganization";
import {
  KYDTicket,
  KYDTicketType,
  KYDWhitelabelTicketType,
  AdminTicketType,
  Price,
  KYDTicketTypeAccessCode,
  KYDTicketTypePool,
  KYDDashboardWaitlistBuyOrder,
  KYDJournalEntry,
  KYDHold,
  KYDTicketTypeAccessCodeMetrics,
  KYDFee,
} from "./KYDTicket";
import { KYDPriceDisplayMode, KYDPromoCode, KYDPromoCodeMode } from "./KYDCart";
import { SettlementReportTicketTypePrice } from "services/eventService";
import { BodyDoc } from "./KYDCampaign";
import { KYDScope } from "./ICognito";
import type { PurchaseMetricsLineageV1 } from "./PurchaseMetricsLineage";

export interface KYDEvent {
  id: string;
  name: string;
  subtitle?: string;
  description: string;
  description_rich?: BodyDoc;
  organization_id: string;
  status: KYDEventStatus;
  listed: boolean;
  status_secondary?: KYDEventStatusSecondary;
  created_at: string;
  start_at: string;
  end_at: string;
  ticket_types: KYDTicketType[];
  timezone: string;
  currency: Dinero.Currency;
  venues: [Partial<KYDVenue>];
  performers: KYDPerformerDisplay[];
  genres: KYDGenre[];
  display_start_at: string;
  display_end_at: string;
  express_checkout: ("applepay" | "googlepay")[];
  display_mode: "normal" | "rsvp";
  link: string;
  image?: string;
  story?: KYDStory;
  highlight_color?: string;
  background_image?: string;
  mint_authority?: PublicKey;
  collection?: PublicKey;
  facebook?: string;
  instagram?: string;
  twitter?: string;
  organization?: KYDOrganizationDisplay;
  faq_link?: string;
  on_chain_metadata?: AptosTokenDataId;
  aptos_collection?: AptosCollection;
  solana_data?: SolanaData;
  pixels?: KYDPixels;
  marketing_pixels: KYDPixelSettings[];
  short_link?: string;
  bio_link?: string;
  display_options?: KYDEventDisplayOptions;
  short_links?: KYDShortLinks;
  square_id?: string;
  sqaure_image_id?: string;
  sales_tax?: Tax;
  taxes?: TaxBreakdown[];
  doors_at?: string;
  display_doors_at?: string;
  announce_at?: string;
  display_announce_at?: string;
  on_sale_at?: string;
  on_sale_at_override?: string;
  display_on_sale_at?: string;
  off_sale_at?: string;
  display_off_sale_at?: string;
  stripe?: { publishable_key: string };
  sold_out: boolean;
  is_external: boolean;
  related_shows: KYDEventSummary[];
  tix_id?: string;
  age_restriction?: string;
  tags?: string[];
}

export interface KYDWhitelabelEvent extends Omit<KYDEvent, "ticket_types"> {
  ticket_types: KYDWhitelabelTicketType[];
}

export interface KYDStoryAsset {
  key: string;
  position: number;
  url?: string;
}

export interface KYDStory {
  items: KYDStoryAsset[];
  created_at: string;
}

export interface Tax {
  rate: number;
}

export interface TaxBreakdown {
  rate: number;
  type: TaxType;
}

export enum TaxType {
  TICKET_SALES_TAX = "ticket_sales_tax",
  SERVICE_SALES_TAX = "service_sales_tax",
}

export interface KYDGenre {
  id: string;
  name: string;
  created_at: string;
}

export interface KYDShortLinks {
  wallet: string;
  tickets: string;
  bio: string;
  event: string;
  abandoned_sms: string;
}

export interface KYDEventSummary {
  id: string;
  organization_id: string;
  listed: boolean;
  name: string;
  subtitle?: string;
  image: string;
  display_start_at: string;
  start_at: string;
  end_at: string;
  venue: Partial<KYDVenue>;
  venues: Partial<KYDVenue>[];
  link: string;
  timezone: string;
  display_options?: KYDEventDisplayOptions;
  display_mode: KYDEvent["display_mode"];
  currency: KYDEvent["currency"];
  status: KYDEvent["status"];
  is_external: boolean;
  announce_at?: string;
  on_sale_at?: string;
  off_sale_at?: string;
  sold_out?: boolean;
  age_restriction?: string;
  sales_tax?: Tax;
  taxes?: TaxBreakdown[];
}

export interface KYDAdminEventSummary extends KYDEventSummary {
  // added
  display_end_at?: string;
  total_gmv_display?: KYDAdminEvent["total_gmv_display"];
  total_service_fees_display?: KYDAdminEvent["total_service_fees_display"];
  realtime_metrics?: KYDAdminEvent["realtime_metrics"];
  tickets?: KYDAdminEvent["realtime_metrics"]["tickets"];
  tickets_display?: KYDAdminEvent["realtime_metrics"]["tickets_display"];
  total_tickets_purchased?: KYDAdminEvent["total_tickets_purchased"];
  total_tickets_purchased_display?: KYDAdminEvent["total_tickets_purchased_display"];
  total_waitlist_buy_orders?: KYDAdminEvent["realtime_metrics"]["waitlist_buy_orders"];
  total_waitlist_buy_orders_display?: KYDAdminEvent["realtime_metrics"]["waitlist_buy_orders_display"];
  total_tickets?: KYDAdminEvent["realtime_metrics"]["quantity"];
  organization?: KYDOrganizationDisplay;
  performers?: KYDPerformerSearch[];
  scopes?: KYDScope[];
}

export interface KYDEventRealtimeMetrics {
  sales: Price;
  sales_display: string;
  quantity: number;
  quantity_display: string;
  tickets: number;
  tickets_display: string;
  waitlist_buy_orders: number;
  waitlist_buy_orders_display: string;
  waitlist_buy_tickets: number;
  waitlist_buy_tickets_display: string;
  ticket_types: {
    [ticket_type_id: KYDTicketType["id"]]: {
      sales: Price;
      sales_display: string;
      tickets: number;
      tickets_display: string;
      quantity: number;
      quantity_display: string;
      waitlist_buy_orders: number;
      waitlist_buy_orders_display: string;
      waitlist_buy_tickets: number;
      waitlist_buy_tickets_display: string;
      waitlist_buy_pool_orders: number;
      waitlist_buy_pool_orders_display: string;
      waitlist_buy_pool_tickets: number;
      waitlist_buy_pool_tickets_display: string;
      discounts: Price;
      discounts_display: string;
      prices?: {
        name: string;
        unit_price: Price;
        unit_price_display: string;
        price: Price;
        price_display: string;
        quantity: number;
        quantity_display: string;
        source: string;
        type?: KYDEventSettlementItem["type"];
      }[];
    };
  };
  access_codes?: Record<string, KYDTicketTypeAccessCodeMetrics>;
}

export interface KYDEventDisplayOptions {
  hide_in_bio?: boolean;
  hide_event_subheader?: boolean;
  price_mode?: KYDPriceDisplayMode;
  show_currency?: boolean;
  multi_day?: boolean;
  redirect?: KYDEventRedirectOptions;
  show_on_sale_reminder?: boolean;
  mapbox?: { publishable_key: string };
  checkout_disclosure?: string;
  show_code_cta?: boolean;
  attendees_hosted?: string;
  selling_fast?: string;
  screenshot_disabled?: boolean;
}

export interface KYDAdminEventDisplayOptions extends KYDEventDisplayOptions {
  delete_action?: ("ARCHIVE" | "DELETE" | "CANCEL")[];
  guestlist_template_url?: string;
}

export interface KYDEventRedirectOptions {
  on_purchase?: string;
}

export interface KYDPixels {
  facebook?: KYDPixelSettings;
  hubspot?: KYDPixelSettings;
  twitter?: KYDTwitterPixelSettings;
  kyd?: KYDPixelSettings;
}

export interface KYDPixelSettings {
  id: string;
  type:
    | "facebook"
    | "tiktok"
    | "twitter"
    | "hubspot"
    | "snapchat"
    | "ga"
    | "hive"
    | "goaudience";
  access_token?: string;
  event_id?: string;
  ad_account_id?: string;
}

export type KYDPixelSettingsUpdate = Omit<KYDPixelSettings, "access_token"> & {
  /**
   * Omit or send an empty string to preserve the stored token.
   * Send null to clear it, or a non-empty string to replace it.
   */
  access_token?: string | null;
};

export type KYDPixelDisplaySettings = Omit<KYDPixelSettings, "access_token"> & {
  has_access_token: boolean;
};

export interface KYDTwitterPixelSettings extends KYDPixelSettings {
  event_purchase_id?: string;
  event_add_to_cart_id?: string;
  event_initiate_checkout_id?: string;
}

export interface KYDBeehiivConfig {
  publication_id: string;
  api_key: string;
}

export interface KYDEventUpdate {
  id: string;
  status: "PENDING" | "COMPLETE";
  error?: string;
}

export interface AptosTokenDataId {
  collection_name: string;
  token_name: string;
}

export interface AptosCollection {
  creator: string;
  collection_name: string;
  version: number;
}

export interface SolanaData {
  authority: TurnkeySubOrganization;
  collection_mint: TurnkeySubOrganization;
  metadata_uri?: string;
}

export interface TurnkeyWallet {
  public_key: string;
  organization_id: string;
}

export interface AptosToken {
  creator: string;
  collection_name: string;
  token_name: string;
  version: number;
}

export interface SolanaToken {
  creator: string | null;
  asset_id: string | null;
  merkle_tree: string | null;
  leaf_index: number | null;
  collection_mint: string | null;
  authority: string | null;
  metadata_uri: string | null;
  version: number;
}

export interface KYDAdminEvent extends KYDEvent {
  ticket_types: AdminTicketType[];
  tiered_ticket_types: AdminTicketType[];
  promo_codes: KYDPromoCode[];
  max_gmv: number;
  total_gmv: number;
  total_gmv_display: string;
  total_refund_gmv: number;
  total_refund_gmv_display: string;
  total_dispute_gmv: number;
  total_dispute_gmv_display: string;
  total_tickets_purchased: number;
  total_tickets_purchased_display: string;
  total_tickets_purchased_percent: number;
  total_tickets_purchased_percent_display: string;
  total_attendees: number;
  total_attendees_display: string;
  total_discounts: number;
  total_discounts_display: string;
  total_payout: number;
  total_payout_display: string;
  total_boxoffice_cash: number;
  total_boxoffice_cash_display: string;
  metrics_last_updated_at_display: string;
  access_code: string;
  affiliates: KYDAffiliate[];
  organization?: KYDOrganizationFull;
  total_service_fees?: number;
  total_service_fees_display?: string;
  report_link?: string;
  affiliates_report_link?: string;
  latest_update?: KYDEventUpdate;
  campaigns?: KYDAdminCampaign[];
  preview_url?: string;
  algolia_search_key?: string;
  algolia?: AlgoliaSettings;
  breakeven?: Price;
  display_options?: KYDAdminEventDisplayOptions;
  report_recipients?: TicketReportRecipients;
  access_codes: KYDTicketTypeAccessCode[];
  linked_organizations: KYDOrganizationEventLink[];
  realtime_metrics?: KYDEventRealtimeMetrics;
  demographics?: KYDEventDemographics;
  conversion_funnel?: KYDEventConversionFunnel;
  system_ticket_type?: KYDTicketType;
  reports?: KYDEventReports;
  avg_tickets_per_order?: number;
  avg_tickets_per_order_display?: string;
  avg_age?: number;
  avg_age_display?: string;
  payouts?: KYDJournalEntry[];
  scheduled_updates?: KYDEventScheduledUpdateSummary[];
  confirmation_emails?: KYDEventConfirmationEmail[];
  fees_override?: KYDOrganizationFee[];
}

/**
 * Event root record metadata as stored in DynamoDB.
 * This represents the metadata field of the root record where pk = sk = EVENT#${event_id}
 */
export interface KYDEventRootMetadata {
  id: string;
  algolia_search_key?: string;
  announce_at?: string;
  aptos_collection?: AptosCollection;
  bio_link?: string;
  created_at: string;
  currency: Dinero.Currency;
  description: string;
  description_rich?: BodyDoc;
  display_mode?: "normal" | "rsvp";
  display_options?: KYDEventDisplayOptions;
  doors_at?: string;
  end_at: string;
  express_checkout?: ("applepay" | "googlepay")[];
  genres?: KYDGenre[];
  highlight_color?: string;
  background_image?: string;
  image?: string;
  link: string;
  listed?: boolean;
  name: string;
  off_sale_at?: string;
  on_sale_at?: string;
  on_sale_at_override?: string;
  on_chain_metadata?: AptosTokenDataId;
  organization_id: string;
  performers?: KYDPerformerDisplay[];
  pixels?: KYDPixels;
  marketing_pixels?: KYDPixelSettings[];
  sales_tax?: Tax;
  taxes?: TaxBreakdown[];
  short_link?: string;
  short_links?: KYDShortLinks;
  solana_data?: SolanaData;
  sold_out?: boolean;
  is_external?: boolean;
  square_id?: string;
  sqaure_image_id?: string;
  start_at: string;
  status: KYDEventStatus;
  story?: KYDStory;
  subtitle?: string;
  timezone: string;
  tix_id?: string;
  venues?: Partial<KYDVenue>[];
  related_shows?: KYDEventSummary[];
  age_restriction?: string;
  tags?: string[];
}

export interface ddb_KYDEventScheduledUpdates {
  unique_dates: number;
  dates: {
    [date: string]: {
      poster?: string;
      description?: string;
      description_rich?: BodyDoc;
      performers?: KYDPerformerDisplay[];
      name?: string;
      subtitle?: string;
    };
  };
  created_at: string;
  updated_at: string;
}

export interface KYDEventScheduledUpdateSummary {
  id: string;
  date: string;
  description?: string;
  description_rich?: BodyDoc;
  poster?: string;
  performers?: KYDPerformerDisplay[];
  name?: string;
  subtitle?: string;
}

export type KYDEventReportType =
  | "tickets"
  | "waitlist"
  | "settlement"
  | "settlement-pdf"
  | "sales-pdf"
  | "guestlist";

export type KYDEventReports = {
  [K in KYDEventReportType]?: KYDEventReport;
};

export interface KYDEventReport {
  url: string;
  updated_at: string;
}

export interface KYDEventDemographics {
  gender: KYDEventDetailDemographicsItem[];
  age: KYDEventDetailDemographicsItem[];
  age_average: number;
}
export interface KYDEventConversionFunnel {
  steps: {
    rate: number;
    rate_display: string;
    count: number;
    count_display: string;
    event_name: string;
    label: string;
  }[];
}

// EVENT DETAIL SECTIONS
export type KYDAdminEventDetailNavIcon =
  | "MdEvent"
  | "IoPeopleOutline"
  | "IoShareSocialOutline"
  | "IoBarChartOutline"
  | "IoSettingsOutline"
  | "IoClipboardOutline"
  | "IoAnalytics"
  | "IoStopwatchOutline"
  | "IoCashOutline"
  | "default";
export interface KYDAdminEventDetailNav {
  type: "link";
  url: string;
  icon: KYDAdminEventDetailNavIcon;
  text: string;
  sections: KYDAdminEventDetailSection[];
}

export enum KYDAdminEventDetailSectionsTypeEnum {
  SUMMARY = "summary",
  TICKET_SALES = "ticket_sales",
  TIMELINES = "timelines",
  LINEUP = "lineup",
  TICKET_TYPES = "ticket_types",
  ACCESS_CODES = "access_codes",
  STORY = "story",
  PROMO_CODES = "promo_codes",
  AFFILIATES = "affiliates",
  DESCRIPTION = "description",
  MARKETING_LINKS = "marketing_links",
  CAMPAIGNS = "campaigns",
  ATTENDEES = "attendees",
  PIXELS = "pixels",
  DEMOGRAPHICS = "demographics",
  CONVERSION_FUNNEL = "conversion_funnel",
  GUESTLIST = "guestlist",
  WAITLIST = "waitlist",
  STATS = "stats",
  PAYOUTS = "payouts",
  CONFIRMATION_EMAILS = "confirmation_emails",
  SETTINGS = "settings",
}

export type KYDAdminEventDetailSectionsType =
  | "summary"
  | "ticket_sales"
  | "timelines"
  | "lineup"
  | "ticket_types"
  | "access_codes"
  | "story"
  | "promo_codes"
  | "affiliates"
  | "description"
  | "marketing_links"
  | "campaigns"
  | "attendees"
  | "pixels"
  | "demographics"
  | "conversion_funnel"
  | "guestlist"
  | "waitlist"
  | "settlement_report"
  | "stats"
  | "payouts"
  | "ticket_holds"
  | "scheduled_updates"
  | "confirmation_emails"
  | "settings";

export type KYDAdminEventDetailSectionPermissions = Array<"write" | "read">;
export interface KYDAdminEventDetailSection {
  type: KYDAdminEventDetailSectionsType;
  data: unknown;
  permissions: KYDAdminEventDetailSectionPermissions;
}

export interface EventDetailData {
  id: KYDAdminEvent["id"];
  timezone: KYDAdminEvent["timezone"];
  currency: KYDAdminEvent["currency"];
  link: KYDAdminEvent["link"];
  short_link: KYDAdminEvent["short_link"];
  status: KYDAdminEvent["status"];
  subtitle?: KYDAdminEvent["subtitle"];
}

export interface EventDetailSummarySectionData extends KYDEventSummary {
  tags?: string[];
  display_end_at: string;
  preview_url: string;
  short_link: string;
  currency: KYDAdminEvent["currency"];
  status: KYDAdminEvent["status"];
  scheduled_updates: KYDEventScheduledUpdateSummary[];
  poster: string;
  description: string;
  description_rich?: BodyDoc;
  performers: KYDPerformerDisplay[];
  mapbox: {
    publishable_key: string;
  };
}
export interface EventDetailSummarySection extends KYDAdminEventDetailSection {
  type: "summary";
  // Might need some tweaks for hiding certain data
  data: EventDetailSummarySectionData;
}

export interface EventDetailTicketSalesSectionData extends EventDetailData {
  waitlist_metrics?: Pick<
    KYDAdminEvent["realtime_metrics"],
    | "waitlist_buy_orders"
    | "waitlist_buy_orders_display"
    | "waitlist_buy_tickets"
    | "waitlist_buy_tickets_display"
  >;
}
export interface EventDetailTicketSalesSection
  extends KYDAdminEventDetailSection {
  type: "ticket_sales";
  // Might need some tweaks for hiding certain data
  data: EventDetailTicketSalesSectionData;
}

export interface EventDetailTicketHoldsSectionData extends EventDetailData {
  ticket_holds: KYDHold[];
  ticket_type_pools: KYDTicketTypePool[];
  ticket_types: AdminTicketType[];
  access_codes: KYDTicketTypeAccessCode[];
}
export interface EventDetailTicketHoldsSection
  extends KYDAdminEventDetailSection {
  type: "ticket_holds";
  // Might need some tweaks for hiding certain data
  data: EventDetailTicketHoldsSectionData;
}

export interface EventDetailTimelinesSectionData extends EventDetailData {
  announce_at?: string;
  on_sale_at?: string;
  start_at?: string;
  off_sale_at?: string;
  end_at?: string;
  venue?: Partial<KYDVenue>;
  image?: string;
  event_name?: string;
  event_url?: string;
  related_shows?: KYDEventSummary[];
  ticket_types?: KYDTicketType[];
}
export interface EventDetailTimelinesSection
  extends KYDAdminEventDetailSection {
  type: "timelines";
  data: EventDetailTimelinesSectionData;
}

export interface EventDetailLineupSectionData extends EventDetailData {
  name: string;
  description: string;
  poster: string;
  timezone: KYDAdminEvent["timezone"];
  performers: KYDAdminEvent["performers"];
  genres: KYDAdminEvent["genres"];
  scheduled_updates: KYDEventScheduledUpdateSummary[];
}
export interface EventDetailLineupSection extends KYDAdminEventDetailSection {
  type: "lineup";
  data: EventDetailLineupSectionData;
}

export interface EventDetailDescriptionSectionData extends EventDetailData {
  description: KYDAdminEvent["description"];
  description_rich?: KYDAdminEvent["description_rich"];
  poster: string;
  scheduled_updates: KYDEventScheduledUpdateSummary[];
}
export interface EventDetailDescriptionSection
  extends KYDAdminEventDetailSection {
  type: "description";
  data: EventDetailDescriptionSectionData;
}

export interface EventDetailGuestlistSection
  extends KYDAdminEventDetailSection {
  type: "guestlist";
  data: EventDetailData;
}

export interface EventDetailWaitlistSectionData extends EventDetailData {
  realtime_metrics: {
    waitlist_buy_orders: KYDAdminEvent["realtime_metrics"]["waitlist_buy_orders"];
    waitlist_buy_orders_display: KYDAdminEvent["realtime_metrics"]["waitlist_buy_orders_display"];
    waitlist_buy_tickets: KYDAdminEvent["realtime_metrics"]["waitlist_buy_tickets"];
    waitlist_buy_tickets_display: KYDAdminEvent["realtime_metrics"]["waitlist_buy_tickets_display"];
  };
  waitlist_buy_orders: KYDDashboardWaitlistBuyOrder[];
}

export interface EventDetailWaitlistSection extends KYDAdminEventDetailSection {
  type: "waitlist";
  data: EventDetailWaitlistSectionData;
}

export interface EventDetailPayoutsSectionItem {
  financial_action_id: string;
  created_at: string;
  hold_amount: string;
  payout_amount: string;
  rebate_amount?: string;
}
export interface EventDetailPayoutsSectionData extends EventDetailData {
  journal_entries: EventDetailPayoutsSectionItem[];
}
export interface EventDetailPayoutsSection extends KYDAdminEventDetailSection {
  type: "payouts";
  data: EventDetailPayoutsSectionData;
}

export interface EventDetailTicketTypesSectionData extends EventDetailData {
  report_link?: string;
  hasTimelinesSet: boolean;
  on_sale_at: string;
  off_sale_at: string;
  end_at: string;
  start_at: string;
  ticket_types: AdminTicketType[];
  tiered_ticket_types: AdminTicketType[];
  event_defaults?: KYDEventDefaults;
  realtime_metrics?: KYDAdminEvent["realtime_metrics"];
}
export interface EventDetailTicketTypesSection
  extends KYDAdminEventDetailSection {
  type: "ticket_types";
  data: EventDetailTicketTypesSectionData;
}
export interface EventDetailScheduledUpdatesSectionData
  extends EventDetailData {
  name: string;
  subtitle?: string;
  description: string;
  description_rich?: BodyDoc;
  poster: string;
  performers: KYDPerformerDisplay[];
  ticket_types: AdminTicketType[];
  tiered_ticket_types: AdminTicketType[];
  scheduled_updates: KYDEventScheduledUpdateSummary[];
}

export interface EventDetailScheduledUpdatesSection
  extends KYDAdminEventDetailSection {
  type: "scheduled_updates";
  data: EventDetailScheduledUpdatesSectionData;
}

export interface EventDetailAccessCodesSectionData extends EventDetailData {
  ticket_types: AdminTicketType[];
  tiered_ticket_types: AdminTicketType[];
  ticket_holds: KYDHold[];
  access_codes: KYDAdminEvent["access_codes"];
  realtime_metrics?: KYDAdminEvent["realtime_metrics"];
}

export interface EventDetailAccessCodesSection
  extends KYDAdminEventDetailSection {
  type: "access_codes";
  data: EventDetailAccessCodesSectionData;
}

export interface EventDetailStorySectionData extends EventDetailData {
  story: KYDStory;
}

export interface EventDetailStorySection extends KYDAdminEventDetailSection {
  type: "story";
  data: EventDetailStorySectionData;
}

export interface EventDetailPromocodesSectionData extends EventDetailData {
  promo_codes: KYDAdminEvent["promo_codes"];
  ticket_types: AdminTicketType[];
  tiered_ticket_types: AdminTicketType[];
}
export interface EventDetailPromocodesSection
  extends KYDAdminEventDetailSection {
  type: "promo_codes";
  data: EventDetailPromocodesSectionData;
}

export interface EventDetailAffiliatesSectionData extends EventDetailData {
  affiliates_report_link?: KYDAdminEvent["affiliates_report_link"];
  affiliates: KYDAdminEvent["affiliates"];
}
export interface EventDetailAffiliatesSection
  extends KYDAdminEventDetailSection {
  type: "affiliates";
  data: EventDetailAffiliatesSectionData;
}

export interface EventDetailCampaignsSectionData extends EventDetailData {
  campaigns: KYDAdminEvent["campaigns"];
}
export interface EventDetailCampaignsSection
  extends KYDAdminEventDetailSection {
  type: "campaigns";
  data: EventDetailCampaignsSectionData;
}

export interface EventDetailAttendeesSectionData extends EventDetailData {
  ticket_types: KYDTicketType[];
  system_ticket_type: KYDTicketType;
  algolia: {
    app_id: string;
    index_name: string;
    api_key: string;
  };
}

export interface EventDetailAttendeesSection
  extends KYDAdminEventDetailSection {
  type: "attendees";
  data: EventDetailAttendeesSectionData;
}

export interface EventDetailMarketingLinksSectionData extends EventDetailData {
  organization: {
    slug_id: string;
  };
}
export interface EventDetailMarketingLinksSection
  extends KYDAdminEventDetailSection {
  type: "marketing_links";
  data: EventDetailMarketingLinksSectionData;
}

export interface EventDetailPixelsSectionData extends EventDetailData {
  marketing_pixels: KYDPixelDisplaySettings[];
  organization_marketing_pixels: KYDPixelDisplaySettings[];
}
export interface EventDetailPixelsSection extends KYDAdminEventDetailSection {
  type: "pixels";
  data: EventDetailPixelsSectionData;
}

export interface KYDEventDetailDemographicsItem {
  label: string;
  value: number;
  percentage?: number;
}

export interface EventDetailDemographicsSectionData extends EventDetailData {
  demographics: {
    gender?: KYDEventDetailDemographicsItem[];
    age?: KYDEventDetailDemographicsItem[];
    country?: KYDEventDetailDemographicsItem[];
    city?: KYDEventDetailDemographicsItem[];
  };
}
export interface EventDetailDemographicsSection
  extends KYDAdminEventDetailSection {
  type: "demographics";
  data: EventDetailDemographicsSectionData;
}

export interface EventDetailConversionFunnelSectionData
  extends EventDetailData {
  conversion_funnel: KYDEventConversionFunnel;
}
export interface EventDetailConversionFunnelSection
  extends KYDAdminEventDetailSection {
  type: "conversion_funnel";
  data: EventDetailConversionFunnelSectionData;
}

export interface EventDetailSettlementReportSectionData
  extends EventDetailData {
  settlement_report: {
    ticket_types: {
      name: string;
      prices: SettlementReportTicketTypePrice[];
    }[];
    total_by_source: SettlementReportTicketTypePrice[];
  };
}

export interface EventDetailSettlementReportSection
  extends KYDAdminEventDetailSection {
  type: "settlement_report";
  data: EventDetailSettlementReportSectionData;
}

export interface EventDetailStatsSectionData extends EventDetailData {
  stats: KYDEventStat[];
}
export interface EventDetailStatsSection extends KYDAdminEventDetailSection {
  type: "stats";
  data: EventDetailStatsSectionData;
}

export interface KYDEventConfirmationEmail {
  id: string; // Starts with 'ON'
  event_id: string;
  name: string;
  note: string;
  note_rich?: BodyDoc;
  ticket_type_ids: string[];
  created_at: string;
  updated_at: string;
}

export interface EventDetailConfirmationEmailSectionData
  extends EventDetailData {
  confirmation_emails: KYDEventConfirmationEmail[];
  ticket_types: AdminTicketType[];
}

export interface EventDetailConfirmationEmailsSection
  extends KYDAdminEventDetailSection {
  type: "confirmation_emails";
  data: EventDetailConfirmationEmailSectionData;
}

export interface EventDetailSettingsSectionData extends EventDetailData {
  display_options: KYDAdminEventDisplayOptions;
  tags?: string[];
  event_rebate?: {
    mode: "percentage" | "fixed";
    amount: number; // In bips (percentage) or cents (fixed)
  } | null;
  organization_rebate?: {
    mode: "percentage" | "fixed";
    amount: number; // In bips (percentage) or cents (fixed)
  } | null;
  fees?: KYDOrganizationFee[]; // Merged fees (organization + event, event takes precedence)
}

export interface EventDetailSettingsSection extends KYDAdminEventDetailSection {
  type: "settings";
  data: EventDetailSettingsSectionData;
}

export interface KYDAdminEventDetail extends KYDAdminEvent {
  nav: KYDAdminEventDetailNav[];
}
// end: EVENT DETAIL SECTIONS

export enum ReportMode {
  DAILY = "daily",
  WEEKLY = "weekly",
}
export interface TicketReportRecipients {
  recipients: string[];
  mode: ReportMode;
  custom_days?: string[];
  report_time?: string;
}
export interface AlgoliaSettings {
  app_id: string;
  api_key: string;
  index_name: string;
}

export interface KYDAttendingEvent extends KYDEvent {
  tickets: KYDTicket[];
}

export interface CreateKYDEventRequest {
  name: string;
  start_at: string;
  end_at: string;
  organization_id: string;
  timezone: string;
  description: string;
  currency: Dinero.Currency;
  duration: string;
  highlight_color?: string;
  image?: string;
  background_image?: string;
  faq_link: string;
  venue_id: string;
  performer_id?: string;
  free: boolean;
  demo?: boolean;
  event_id_override?: string;
  display_mode?: KYDEvent["display_mode"];
}

export interface CreateKYDEventRequestV2 {
  name: string;
  // start_at: string;
  // duration: string;
  // venue_name: string;
  // venue_city: string;
  subtitle?: string;
  demo?: boolean;
  event_id_override?: string;
  display_mode?: KYDEvent["display_mode"];
  age_restriction?: KYDEvent["age_restriction"];
  duplicate_event_id?: string;
}

export interface UpdateTicketTypeRequest {
  id?: string;
  name?: string;
  price?: string;
  quantity?: string;
  pool?: KYDTicketTypePool;
  on_sale_at?: string;
  off_sale_at?: string;
  start_at?: string;
  end_at?: string;
  code?: string;
  description?: string;
  increment?: string;
  waitlist_status?: KYDTicketType["waitlist_status"];
  waitlist_return?: KYDTicketType["waitlist_return"];
  shift_quantity_on_expiration?: boolean;
  expires_at?: string;
  limit?: string;
  status?: KYDTicketType["status"];
  sold_out_finalized?: boolean;
  last_ledger_entry_index?: number;
}

export interface UpdateTicketTypesRequest {
  id?: string;
  quantity?: string;
  limit?: string;
  price?: string;
  name?: string;
  on_sale_at?: string;
  off_sale_at?: string;
  start_at?: string;
  end_at?: string;
  code?: string;
  description?: string;
  waitlist_status?: KYDTicketType["waitlist_status"];
  increment?: string;
  shift_quantity_on_expiration?: boolean;
  expires_at?: string;
  ticket_types?: UpdateTicketTypeRequest[];
  last_ledger_entry_index?: number;
  waitlist_return?: KYDTicketType["waitlist_return"];
}

export interface UpdateKYDEventRequest extends UpdateTicketTypesRequest {
  type?: EventUpdateRequest;
  admin_id?: string;
  event_id: KYDEvent["id"];
  ticket_type_id?: KYDTicketType["id"];
  algolia_search_key?: string;
  request: {
    admin_id?: string;
    algolia_search_key?: string;
    quantity?: string;
    name?: string;
    type?: EventUpdateRequest;
    key?: EventUpdateRequest | "timeline";
    ticket_types?: UpdateTicketTypesRequest[];
    poster?: string;
    description?: string;
    description_rich?: BodyDoc;
    image?: string;
    venue?: string;
    start_at?: string;
    end_at?: string;
    doors_at?: string;
    on_sale_at?: string;
    announce_at?: string;
    timeline?: string;
    breakeven?: string;
    performers?: KYDPerformerDisplay[];
    related_shows?: KYDEventSummary[];
    genres?: KYDGenre[];
    marketing_pixels?: KYDPixelSettings[];
    story?: KYDStory;
    subtitle?: string;
    listed?: boolean;
    display_options?: KYDEventDisplayOptions;
    age_restriction?: string;
    tags?: string[];
    ticket_type_display_order?: string[];
  };
}

export interface CreateDemoEventRequest {
  name: string;
  slug_id: string;
  poster_url: string;
  logo_url: string;
  free: boolean;
  organization_name?: string;
  verified_fans?: number;
}

export interface CreateKYDVenueRequest {
  name: string;
  address: string;
  capacity: number;
  logo_url?: string;
}

export interface CreateKYDPerformerRequest {
  name: string;
  image_url: string;
}

export interface CreateAffiliatesRequest {
  quantity: string;
  entries?: { name: string }[];
  campaign_id?: string;
  editable?: boolean;
}

export interface KYDAffiliate {
  id: string;
  event_id: string;
  url_id: string;
  clicks: number;
  gmv: number;
  quantity: number;
  url: string;
  created_at: string;
  updated_at: string;
  display_clicks: string;
  display_revenue: string;
  status: "OPEN" | "DELETED";
  email?: string;
  name?: string;
  campaign_id?: string;
  editable?: boolean;
}

export interface KYDEventMetricPoint {
  date: string;
  currency: string;
  gmv: number;
  gmv_display: string;
  service_fees: number;
  tickets_purchased: number;
  total_attendees: number;
  updated_at: string;
  direct: number;
}

export interface KYDEventMetrics {
  metrics: KYDEventMetricPoint[];
  bars: any[];
  tickets_sold_display: string;
  stats: KYDEventStat[];
}

export interface KYDEventStat {
  title: string;
  value: string;
}

export interface CreatePromoCodeRequest {
  code: string;
  amount: string;
  ticket_type_ids: string[];
  mode: KYDPromoCodeMode;
  enabled: boolean;
  expires_at?: string;
  max_total_uses?: string;
  max_uses_per_user?: string;
}

export interface CreateAccessCodeRequest {
  code: string;
  ticket_type_ids: string[];
  status?: KYDTicketTypeAccessCode["status"];
  valid_from?: string;
  valid_to?: string;
}

export interface UpdatePromoCodeRequest {
  code?: string;
  amount?: string;
  ticket_type_ids?: string[];
  mode?: KYDPromoCodeMode;
  enabled?: boolean;
  expires_at?: string;
  max_total_uses?: string;
  max_uses_per_user?: string;
}

export interface UpdateTicketTypeAccessCodeRequest {
  code?: string;
  ticket_type_ids?: string[];
  status?: KYDTicketTypeAccessCode["status"];
  valid_from?: string;
  valid_to?: string;
}

export interface UpdateAffiliateRequest {
  name?: string;
  status?: KYDAffiliate["status"];
}

export interface KYDVenue {
  id: string;
  name: string;
  address: string;
  capacity: number;
  location: KYDPlace;
  link: string;
  timezone: string;
  logo_url?: string;
  city?: string;
  mapbox_id?: string;
  stripe_location_id?: string;
  state?: string;
  state_code?: string;
  country?: string;
  country_code?: string;
}

export interface KYDPlace {
  /**
   * The numerical portion of an address, such as a building number.
   */
  AddressNumber?: string;
  /**
   * The Amazon Location categories that describe this Place. For more information about using categories, including a list of Amazon Location categories, see Categories and filtering, in the Amazon Location Service Developer Guide.
   */
  Categories?: string[];
  /**
   * A country/region specified using ISO 3166 3-digit country/region code. For example, CAN.
   */
  Country?: string;
  /**
   * The ISO-2 code for the Country.
   */
  CountryCode?: string;
  Geometry: { Point: number[] };
  /**
   * The full name and address of the point of interest such as a city, region, or country. For example, 123 Any Street, Any Town, USA.
   */
  Label?: string;
  /**
   * A name for a local area, such as a city or town name. For example, Toronto.
   */
  Municipality?: string;
  /**
   * The name of a community district. For example, Downtown.
   */
  Neighborhood?: string;
  /**
   * A group of numbers and letters in a country-specific format, which accompanies the address for the purpose of identifying a location.
   */
  PostalCode?: string;
  /**
   * A name for an area or geographical division, such as a province or state name. For example, British Columbia.
   */
  Region?: string;
  /**
   * The ISO-2 code for the Region.
   */
  RegionCode?: string;
  /**
   * The name for a street or a road to identify a location. For example, Main Street.
   */
  Street?: string;
  /**
   * An area that's part of a larger municipality. For example, Blissville is a submunicipality in the Queen County in New York.  This property is only returned for a place index that uses Esri as a data provider. The property is represented as a district.  For more information about data providers, see Amazon Location Service data providers.
   */
  SubMunicipality?: string;
  /**
   * A county, or an area that's part of a larger region. For example, Metro Vancouver.
   */
  SubRegion?: string;
  /**
   * The time zone in which the Place is located. Returned only when using HERE or Grab as the selected partner.
   */
  TimeZone?: string;
  /**
   * For addresses with multiple units, the unit identifier. Can include numbers and letters, for example 3B or Unit 123.  This property is returned only for a place index that uses Esri or Grab as a data provider. It is not returned for SearchPlaceIndexForPosition.
   */
  UnitNumber?: string;
  /**
   * For addresses with a UnitNumber, the type of unit. For example, Apartment.  This property is returned only for a place index that uses Esri as a data provider.
   */
  UnitType?: string;
}

export interface KYDPerformer {
  id: string;
  soundcharts_id?: string;
  name: string;
  image_url: string;
  created_at: string;
  updated_at: string;
  profiles: KYDPerformerProfile[];
}

export interface KYDPerformerDisplay {
  id: string;
  name: string;
  image_url: string;
  headliner: boolean;
  set_time?: string;
  spotify?: KYDPerformerProfile;
  instagram?: KYDPerformerProfile;
  tiktok?: KYDPerformerProfile;
  isni?: KYDPerformerProfile;
}

export interface KYDPerformerSearch {
  id: string;
  name: string;
  image_url: string;
  headliner: boolean;
}

export interface KYDPerformerProfile {
  name: string;
  followers: number;
  display_followers: string;
  url: string;
  platform: "instagram" | "tiktok" | "spotify" | "isni" | "bandsintown";
  id: string;
}

export type SoundchartSearchItem = {
  uuid: string;
  slug: string;
  name: string;
  appUrl: string;
  imageUrl: string;
};

export type SoundchartPlatformItem = {
  platformName: string;
  platformCode: string;
  identifier: string;
  url: string;
  default: boolean;
};

export interface KYDEventHTMLMetadata {
  id: string;
  name: string;
  organization_id: string;
  organization_name: string;
  updated_at: string;
  pixels?: KYDPixels;
  marketing_pixels?: KYDPixelSettings[];
  description?: string;
  image?: string;
  organization_image?: string;
  organization_description?: string;
  venue: { name: string; address: string; latitude: number; longitude: number };
  link: string;
  start_at: string;
  end_at: string;
  performers: KYDPerformer[];
}

export interface KYDMetaCampaignAction {
  action_type: string;
  value: string;
}

export interface KYDCampaign {
  campaign_name: string;
  campaign_id: string;
  objective: string;
  account_id: string;
  spend: string;
  clicks: string;
  impressions: string;
  cpc: string;
  cpm: string;
  ctr: string;
  actions: KYDMetaCampaignAction[];
  date_start: string;
  date_stop: string;
  /**
   * Canonical ROAS for KYD product logic: custom-conversion–scoped purchase value ÷ spend when
   * custom conversion ids apply (see `custom_conversion_ids` / ad set `custom_conversion_id`);
   * otherwise pixel-level / Meta-derived rules. Prefer this field over {@link platform_roas} when reading metrics.
   */
  kyd_roas: string;
  /** Purchase value (cents, string). After `constructCampaignMetadata`, matches Meta resolution — not raw bank `ticket_gmv` on {@link AdminCampaignInsightsMetadata}. */
  ticket_gmv: string;
  ticket_count: string;
  platform_purchase_count: string;
  platform_purchase_action_type?: string;
  platform_purchase_value?: string;
  platform_purchase_value_action_type?: string;
  /** Where purchase / ROAS numbers came from (Meta field + action_type or computed). */
  purchase_metrics_lineage?: PurchaseMetricsLineageV1;
  avg_order_size?: string;
  add_to_cart_count?: string;
  initiate_checkout_count?: string;
  complete_registration_count?: string;
  landing_page_view_count?: string;
  link_click_count?: string;
  platform: KYDMarketingPlatform;
  status: string;
  /**
   * Same numeric string as {@link kyd_roas} when rows are built via `constructCampaignMetadata`; kept for older readers.
   * For new code, use **kyd_roas** — it names the metric that reflects custom conversion scope.
   */
  platform_roas: string;
  roas_gmv: string;
  custom_conversions?: {
    id: string;
    value: string;
    type: "purchases" | "sales";
  }[];
  /**
   * KYD: Meta custom conversion ids for this row (campaign = aggregate of ad sets; ad set/ad = that ad set’s id(s)).
   */
  custom_conversion_ids?: string[];
  events?: {
    id: string;
    purchases: string;
    calculated_roas: number;
    sales: string;
    name: string;
    link: string;
  }[];
}

export interface KYDAdminCampaign extends KYDCampaign {
  campaign_id: string;
  objective: string;
  account_id: string;
  display_spend: string;
  display_clicks: string;
  display_impressions: string;
  display_cpc: string;
  display_cpm: string;
  actions: KYDMetaCampaignAction[];
  date_start: string;
  date_stop: string;
  display_kyd_roas: string;
  display_ticket_gmv: string;
  display_ticket_count: string;
  display_ctr: string;
  display_platform_roas: string;
  display_platform_purchase_value?: string;
  display_avg_order_size?: string;
  last_updated_at: string;
  event_id: string;
  display_roas_gmv: string;
  display_budget_remaining?: string;
  display_lifetime_budget?: string;
  display_daily_budget?: string;
  highest_performing_adset_name?: string;
  display_reach?: string;
  display_frequency?: string;
  display_events: {
    id: string;
    display_ticket_gmv: string;
    display_roas: string;
    name: string;
    link: string;
  }[];
}

export const enum KYDEventStatus {
  DRAFT = "DRAFT",
  PENDING_APPROVAL = "PENDING_APPROVAL",
  OPEN = "OPEN",
  CANCELED = "CANCELED",
  DELETED = "DELETED",
  OFFSALE = "OFFSALE",
  PENDING_ANNOUNCEMENT = "PENDING_ANNOUNCEMENT",
  PREVIEW = "PREVIEW",
  ARCHIVED = "ARCHIVED",
}

export const enum EventUpdateRequest {
  DESCRIPTION = "description",
  DESCRIPTION_RICH = "description_rich",
  POSTER = "poster",
  TICKET_TYPE = "ticket_type",
  TICKET_TYPE_QUANTITY_INCREASE = "ticket_type_quantity_increase",
  TICKET_TYPES = "ticket_types",
  TICKET_TYPES_QUANTITY_INCREASE = "ticket_types_quantity_increase",
  LAUNCH = "launch",
  DATE = "date",
  VENUE = "venue",
  EVENT_NAME = "name",
  PERFORMERS = "performers",
  INCREMENT = "increment",
}

enum KYDEventStatusSecondary {
  Rescheduled,
}

export const enum KYDWaitlistStatusType {
  OPEN = "OPEN",
  LOCKED = "LOCKED",
  DISABLED = "DISABLED",
}

export const enum KYDWaitlistProcessType {
  NORMAL = "NORMAL",
  NO_SELLER = "NO_SELLER",
}

export interface KYDGuestlistState {
  remaining: number;
  allocation: number;
  assigned: number;
  ticket_type_id: string;
  unique_people: number;
  execution_id?: string;
}

export interface KYDGuestlistUser {
  assigned: number;
  created_at: string;
  event_id: string;
  status: string;
  ticket_type_id: string;
  updated_at: string;
  user_id: string;
}

export interface KYDGuestlistDisplayState {
  allocations: {
    remaining: number;
    allocation: number;
    assigned: number;
    ticket_type_id: string;
    is_processing?: boolean;
  }[];
  unique_people?: number;
  execution_id?: string;
}

export interface KYDEventBundle {
  id: string;
  events: KYDEventSummary[];
  display_created_at: string;
  created_at: string;
  updated_at: string;
  title: string;
  organization_id: string;
  organization: KYDOrganizationDisplay;
  marketing_pixels: KYDPixelSettings[];
  link: string;
  description?: string;
}

export type KYDScheduleType =
  | "scheduleactions"
  | "firstreminder"
  | "secondreminder"
  | "adminreminder"
  | "announcement"
  | "ticketreport"
  | "onsalereminder"
  | "announcement"
  | "eventended"
  | `hold.expired.${string}`;

export interface KYDEventConfigDDB {
  event_bus_name: string;
  event_bus_arn: string;
  shift_pool_capacity_lambda_arn: string;
  shift_pool_capacity_access_key_id: string;
  shift_pool_capacity_secret_access_key: string;
}

export type KYDReportDay =
  | "MON"
  | "TUE"
  | "WED"
  | "THU"
  | "FRI"
  | "SAT"
  | "SUN";

export interface KYDPartner {
  name: string;
  api_key: string;
  created_at: string;
  updated_at: string;
  attribution_name: string;
}
